import React, { useState, useEffect } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { subscriptionService, CreateSubscriptionResponse } from '../services/subscriptionService';
import { useAuthContext } from '../context/AuthContext';
import InlineAlert from './InlineAlert';

interface PaymentFormProps {
  priceId: string;
  restaurantId?: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
}

interface PaymentMethod {
  id: string;
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}

export default function PaymentForm({ 
  priceId, 
  restaurantId, 
  onSuccess, 
  onError, 
  isProcessing, 
  setIsProcessing 
}: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { ensureValidToken } = useAuthContext();
  const [error, setError] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponInfo, setCouponInfo] = useState<any>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [existingPaymentMethods, setExistingPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null);

  // Obtener métodos de pago existentes al cargar el componente
  useEffect(() => {
    console.log('🔍 PaymentForm: useEffect ejecutándose...');
    console.log('🔍 PaymentForm: Estado actual:', {
      existingPaymentMethods: existingPaymentMethods.length,
      selectedPaymentMethod,
      showNewCardForm
    });
    fetchExistingPaymentMethods();
  }, []);

  const fetchExistingPaymentMethods = async () => {
    try {
      console.log('🔍 PaymentForm: fetchExistingPaymentMethods ejecutándose...');
      console.log('🔍 PaymentForm: Verificando autenticación...');
      
      // Asegurar que el token esté válido antes de hacer la llamada
      console.log('🔍 PaymentForm: Asegurando token válido...');
      const tokenValid = await ensureValidToken();
      console.log('🔍 PaymentForm: Token válido:', tokenValid);
      
      if (!tokenValid) {
        console.error('❌ PaymentForm: Token no válido, no se pueden obtener métodos de pago');
        setError('Error de autenticación. Por favor, inicia sesión nuevamente.');
        return;
      }
      
      // Verificar si hay token disponible
      const token = localStorage.getItem('authToken') || 'No token found';
      console.log('🔍 PaymentForm: Token disponible:', token ? 'SÍ' : 'NO');
      
      const methods = await subscriptionService.getPaymentMethods();
      console.log('📋 PaymentForm: Métodos de pago obtenidos:', methods);
      // Intentar obtener el método de pago por defecto desde la suscripción activa
      let defaultPaymentMethodId: string | null = null;
      try {
        const subscription = await subscriptionService.getUserSubscription();
        defaultPaymentMethodId = subscription?.defaultPaymentMethod?.id || null;
        setDefaultPaymentMethodId(defaultPaymentMethodId);
        console.log('🔎 Default payment method desde suscripción:', defaultPaymentMethodId);
      } catch (e) {
        console.log('No se pudo obtener la suscripción para determinar el default payment method');
      }

      setExistingPaymentMethods(methods);
      if (methods.length > 0) {
        const preferred = methods.find(m => m.id === defaultPaymentMethodId);
        const toSelect = preferred?.id || methods[0].id;
        setSelectedPaymentMethod(toSelect);
        setShowNewCardForm(false);
        console.log('✅ PaymentForm: Método seleccionado:', toSelect);
      } else {
        console.log('ℹ️ PaymentForm: No hay métodos de pago existentes');
      }
    } catch (error) {
      console.error('❌ PaymentForm: Error obteniendo métodos de pago:', error);
      console.error('❌ PaymentForm: Error completo:', error);
      setError('Error obteniendo métodos de pago. Por favor, intenta nuevamente.');
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setIsValidatingCoupon(true);
    try {
      const couponData = await subscriptionService.validateCoupon(couponCode);
      setCouponInfo(couponData);
      setError(null);
    } catch (error: any) {
      setCouponInfo(null);
      setError(`Cupón inválido: ${error.message}`);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleCouponBlur = () => {
    if (couponCode.trim()) {
      validateCoupon();
    }
  };

  const getCardBrandIcon = (brand: string) => {
    const icons: { [key: string]: string } = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
      jcb: '💳',
      dinersclub: '💳',
      unionpay: '💳'
    };
    return icons[brand.toLowerCase()] || '💳';
  };

  const formatExpiryDate = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      let paymentMethodId = selectedPaymentMethod;

      // Solo crear nuevo payment method si no hay uno seleccionado o si no hay métodos existentes
      if ((!selectedPaymentMethod && showNewCardForm) || existingPaymentMethods.length === 0) {
        console.log('Creando nuevo payment method con Stripe...');
        const { paymentMethod, error: paymentMethodError } = await stripe.createPaymentMethod({
          type: 'card',
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: 'Cliente', // Puedes obtener el nombre del usuario actual
          },
        });

        if (paymentMethodError) {
          console.error('Error creando payment method:', paymentMethodError);
          setError(paymentMethodError.message || 'Error al procesar la tarjeta');
          onError(paymentMethodError.message || 'Error al procesar la tarjeta');
          return;
        }

        paymentMethodId = paymentMethod.id;
        console.log('Payment method creado:', paymentMethodId);
      }

      // 2. Crear suscripción en el backend con payment_method_id
      const subscriptionData: any = {
        price_id: priceId,
        payment_method_id: paymentMethodId
      };

      if (restaurantId) {
        subscriptionData.restaurant_id = restaurantId;
      }

      // Agregar cupón si existe
      if (couponInfo && couponInfo.id) {
        subscriptionData.coupon_id = couponInfo.id;
      }

      console.log('Creando suscripción:', subscriptionData);
      console.log('payment_method_id que se envía:', paymentMethodId);
      console.log('Tipo de payment_method_id:', typeof paymentMethodId);
      const result: CreateSubscriptionResponse = await subscriptionService.createSubscription(subscriptionData);

      console.log('Resultado del backend:', result);

      // 3. Éxito
      console.log('Llamando onSuccess');
      onSuccess();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {/* Métodos de Pago Existentes */}
        {existingPaymentMethods.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Método de Pago
            </label>
            <div className="space-y-2">
              {existingPaymentMethods.map((method) => (
                <label key={method.id} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={method.id}
                    checked={selectedPaymentMethod === method.id}
                    onChange={(e) => { setSelectedPaymentMethod(e.target.value); setShowNewCardForm(false); }}
                    className="text-[#22B6B2] focus:ring-[#22B6B2]"
                  />
                  <div className={`flex items-center space-x-3 p-3 border rounded-lg bg-white flex-1 ${defaultPaymentMethodId === method.id ? 'border-[#22B6B2]' : 'border-gray-200'}`}>
                    <span className="text-2xl">{getCardBrandIcon(method.card.brand)}</span>
                    <div>
                      <div className="font-medium text-gray-900">
                        •••• •••• •••• {method.card.last4}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expira {formatExpiryDate(method.card.exp_month, method.card.exp_year)}
                      </div>
                    </div>
                    {defaultPaymentMethodId === method.id && (
                      <span className="ml-auto text-xs font-semibold px-2 py-1 rounded-full bg-[#22B6B2]/10 text-[#22B6B2] border border-[#22B6B2]/30">
                        Predeterminado
                      </span>
                    )}
                  </div>
                </label>
              ))}
              
              {/* Opción para nueva tarjeta */}
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="new"
                  checked={showNewCardForm}
                  onChange={(e) => {
                    setShowNewCardForm(e.target.checked);
                    setSelectedPaymentMethod('');
                  }}
                  className="text-[#22B6B2] focus:ring-[#22B6B2]"
                />
                <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg bg-white flex-1">
                  <span className="text-2xl">💳</span>
                  <div className="font-medium text-gray-900">
                    Usar nueva tarjeta
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Formulario de Nueva Tarjeta */}
        {(showNewCardForm || existingPaymentMethods.length === 0) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {existingPaymentMethods.length === 0 ? 'Información de Tarjeta' : 'Información de Nueva Tarjeta'}
            </label>
            <div className="border border-gray-300 rounded-lg p-3 bg-white">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                    invalid: {
                      color: '#9e2146',
                    },
                  },
                }}
              />
            </div>
          </div>
        )}

        {/* Campo de Cupón */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Código de Cupón (Opcional)
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              onBlur={handleCouponBlur}
              placeholder="Ingresa tu código de descuento"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#22B6B2] focus:border-transparent"
            />
            <button
              type="button"
              onClick={validateCoupon}
              disabled={!couponCode.trim() || isValidatingCoupon}
              className="px-4 py-2 bg-[#22B6B2] text-white rounded-lg font-medium hover:bg-[#1a9a96] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidatingCoupon ? 'Validando...' : 'Validar'}
            </button>
          </div>
          
          {/* Información del Cupón */}
          {couponInfo && (
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-green-600">✓</span>
                <div>
                  <div className="font-medium text-green-800">
                    Cupón aplicado: {couponInfo.name}
                  </div>
                  <div className="text-sm text-green-600">
                    {couponInfo.percent_off ? `${couponInfo.percent_off}% de descuento` : 
                     couponInfo.amount_off ? `$${(couponInfo.amount_off / 100).toFixed(2)} de descuento` : 
                     'Descuento aplicado'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <InlineAlert type="error" message={error} onClose={() => setError(null)} />
        )}
      </div>

      <button
        type="submit"
        disabled={!stripe || isProcessing || (existingPaymentMethods.length > 0 && !selectedPaymentMethod && !showNewCardForm)}
        className="w-full bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? 'Procesando...' : 'Confirmar Pago'}
      </button>
    </form>
  );
} 